import { Crown, ArrowRight, Sparkles } from "lucide-react";

/**
 * Cross-sell banner to the SMART Royal AI Knight platform.
 *
 * Variants:
 *   - "hero" (default): full-width gold-bordered card with crown + CTA.
 *     Best for LandingPage, MyBookings, ProviderDashboard top-of-page.
 *   - "compact": slim pulsing strip — use inside narrower columns.
 *
 * Branding: matte black, gold (#D4AF37) accents, animated gold sheen + crown
 * pulse so it draws the eye but doesn't shout. Opens in a new tab so users
 * don't lose their booking state.
 */
const ROYAL_AI_URL = "https://knight-hire.preview.emergentagent.com/";

export default function RoyalAIBanner({ variant = "hero", className = "" }) {
    if (variant === "compact") {
        return (
            <a
                href={ROYAL_AI_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="royal-ai-banner-compact"
                className={`
                    relative group flex items-center gap-3 rounded-md
                    px-4 py-3
                    bg-gradient-to-r from-black via-[#1a1505] to-black
                    border border-[#D4AF37]/40
                    hover:border-[#D4AF37]
                    overflow-hidden
                    transition-all duration-300
                    royal-ai-glow
                    ${className}
                `}
            >
                <Crown size={16} className="text-[#D4AF37] shrink-0 royal-ai-crown" strokeWidth={1.5} />
                <div className="text-xs sm:text-sm flex-1 text-foreground">
                    <span className="text-amber-200/90">Royal AI Knight</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className="text-muted-foreground">Hire your back-office assistant</span>
                </div>
                <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="text-[#D4AF37] shrink-0 group-hover:translate-x-1 transition-transform"
                />
                <span className="royal-ai-sheen" aria-hidden="true" />
            </a>
        );
    }

    // hero variant (default)
    return (
        <a
            href={ROYAL_AI_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="royal-ai-banner"
            className={`
                relative group block
                rounded-md overflow-hidden
                bg-gradient-to-br from-black via-[#16110a] to-black
                border border-[#D4AF37]/45
                hover:border-[#D4AF37]
                transition-all duration-500
                royal-ai-glow
                ${className}
            `}
        >
            {/* Animated gold sheen sweep */}
            <span className="royal-ai-sheen" aria-hidden="true" />
            {/* Crown wash bg */}
            <div className="absolute -right-6 -top-6 opacity-10 pointer-events-none">
                <Crown size={140} className="text-[#D4AF37]" strokeWidth={0.5} />
            </div>

            <div className="relative px-6 sm:px-8 py-6 sm:py-7 flex flex-col sm:flex-row sm:items-center gap-5">
                {/* Pulsing crown badge */}
                <div className="shrink-0 relative">
                    <span className="absolute inset-0 rounded-full bg-[#D4AF37]/30 royal-ai-pulse" aria-hidden="true" />
                    <div className="relative w-14 h-14 rounded-full grid place-items-center bg-gradient-to-br from-[#FFD86B] via-[#D4AF37] to-[#8E7100] shadow-[0_8px_22px_-6px_rgba(212,175,55,0.7)]">
                        <Crown size={26} className="text-black" strokeWidth={1.5} />
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Sparkles size={11} className="text-[#D4AF37]" strokeWidth={1.5} />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 font-semibold">
                            Royal Feature · NEW
                        </span>
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl tracking-tight leading-snug text-foreground">
                        Need a <span className="italic gold-grad">Royal AI Knight</span> to handle your back office?
                    </h3>
                    <p className="mt-1.5 text-[12px] sm:text-sm text-muted-foreground leading-relaxed">
                        Meet SMART Royal AI — your always-on assistant for emails, scheduling, follow-ups & admin work.
                    </p>
                </div>

                <div className="shrink-0">
                    <div
                        data-testid="royal-ai-cta"
                        className="
                            inline-flex items-center gap-2
                            px-5 py-3 rounded-sm
                            bg-gradient-to-br from-[#FFD86B] via-[#D4AF37] to-[#8E7100]
                            text-black font-semibold
                            text-[11px] sm:text-xs uppercase tracking-[0.22em]
                            shadow-[0_8px_22px_-8px_rgba(212,175,55,0.85)]
                            group-hover:shadow-[0_12px_30px_-6px_rgba(212,175,55,0.95)]
                            group-hover:scale-[1.03]
                            transition-all duration-300
                        "
                    >
                        Hire Your Assistant
                        <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </div>
            </div>
        </a>
    );
}
